import React, { useEffect, useRef, useState } from 'react';
import { NavigationContainer, useNavigation } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { LoginScreen } from '../screens/Login';
import { Home } from '../screens/Home';
import { Profile } from '../screens/Profile';
import { VenueList } from '../screens/VenueList';
import { VenueScreen } from '../screens/VenueScreen';
import { VenueSubmission } from '../screens/VenueSubmission';
import { Admin } from '../screens/Admin';
import { TourPlanner } from '../screens/TourPlanner';
import { LandingScreen } from '../screens/LandingScreen';
import { HeaderBar } from '../components/HeaderBar';
import { Footer } from '../components/Footer';
import { View, Dimensions, Platform, Text, ScrollView, ActivityIndicator, Image } from 'react-native';
import { config } from '../config/environment';
import { LoginPersistenceProvider, useUser } from '../components/LoginPersistence';
import { VenueProvider } from '../components/VenueContext';
import { CommunityModalProvider } from '../contexts/CommunityModalContext';
import { CommunityProvider } from '../contexts/CommunityContext';
import { AddVenue } from '../screens/AddVenue';
import { MyReports } from '../screens/MyReports';
import { WaitlistScreen } from '../screens/WaitlistScreen';
import { AboutUs } from '../screens/AboutUs';
import { Plans } from '../screens/Plans';
import { SubscriptionSuccess } from '../screens/SubscriptionSuccess';


import { Contact } from '../screens/Contact';
import { CommunityScreen } from '../screens/CommunityScreen';
import CommunityList from '../screens/CommunityList';
import UserProfileScreen from '../screens/UserProfileScreen';
import OrganizationProfileScreen from '../screens/OrganizationProfileScreen';


import * as navigationService from '../services/navigationService';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';

const Stack = createNativeStackNavigator();

const linking = {
  prefixes: ['http://localhost:19006', 'https://yourdomain.com'], // Update with your production domain
  config: {
    screens: {
      Landing: 'landing',
      Login: 'login',
      Home: 'home',
      Profile: 'profile',
      MyReports: 'my-reports',
      VenueList: 'venues',
      Venue: 'venue/:venueId',
      VenueSubmission: 'venue-submission',
      Admin: 'admin',
      AddVenue: 'add-venue',
      Waitlist: 'waitlist',
      TourPlanner: 'tour-planner',
      AboutUs: 'about-us',
      Plans: 'plans',
      SubscriptionSuccess: 'subscription-success',
      Contact: 'contact',
      CommunityScreen: 'community',
      CommunityList: 'community/list',
      UserProfileScreen: 'community/user/:userId'
    },
  },
};

// Auth required wrapper component
function AuthRequired({ children }) {
  const { user, loading } = useUser();
  const navigation = useNavigation();

  useEffect(() => {
    if (!loading && !user) {
      // Redirect to login if not authenticated
      navigation.replace('Login', { 
        message: 'Please sign in to access this feature'
      });
    }
  }, [user, loading, navigation]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Please wait...
        </Text>
      </View>
    );
  }

  if (!user) {
    return null; // Will be redirected in the useEffect
  }

  return children;
}

// Landing screen with header and footer
function LandingScreenContainer(props) {
  return (
    <ScrollView contentContainerStyle={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <HeaderBar />
      <View style={{ flex: 1 }}>
        <LandingScreen {...props} />
      </View>
      <Footer />
    </ScrollView>
  );
}

function ScreenWithHeader({ children, isAdminPage = false, requireAuth = false, onScroll, scrollEventThrottle = 16, footerBg }) {
  if (requireAuth) {
    return (
      <AuthRequired>
        <ScrollView 
          contentContainerStyle={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column'
          }}
          onScroll={onScroll}
          scrollEventThrottle={scrollEventThrottle}
        >
          <HeaderBar />
          <View style={{ flex: 1 }}>
            {children}
          </View>
          <Footer isAdminPage={isAdminPage} backgroundColor={footerBg} />
        </ScrollView>
      </AuthRequired>
    );
  }

  return (
    <ScrollView 
      contentContainerStyle={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column'
      }}
      onScroll={onScroll}
      scrollEventThrottle={scrollEventThrottle}
    >
      <HeaderBar />
      <View style={{ flex: 1 }}>
        {children}
      </View>
      <Footer isAdminPage={isAdminPage} backgroundColor={footerBg} />
    </ScrollView>
  );
}

function HomeScreen(props) {
  return (
    <ScreenWithHeader>
      <Home {...props} />
    </ScreenWithHeader>
  );
}

function ProfileScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <Profile {...props} />
    </ScreenWithHeader>
  );
}

function VenueListScreen(props) {
  const venueListRef = useRef(null);
  
  const handleScroll = (event) => {
    // Call VenueList's scroll handler if it exists
    if (venueListRef.current?.handleScrollInternal) {
      venueListRef.current.handleScrollInternal(event);
    }
  };

  return (
    <ScreenWithHeader onScroll={handleScroll}>
      <VenueList {...props} ref={venueListRef} />
    </ScreenWithHeader>
  );
}

function VenueDetailScreen(props) {
  return (
    <ScreenWithHeader>
      <VenueScreen {...props} />
    </ScreenWithHeader>
  );
}

function VenueSubmissionScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <VenueSubmission {...props} />
    </ScreenWithHeader>
  );
}

function AdminScreen(props) {
  const { user, loading } = useUser();
  const navigation = useNavigation();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Redirect to login if not authenticated
        navigation.replace('Login', {
          message: 'Please sign in to access this feature'
        });
      } else if (user.userType !== 'admin') {
        // Redirect non-admin users away from admin page immediately
        navigation.replace('Home', {
          message: 'Access denied. Admin privileges required.'
        });
      }
    }
  }, [user, loading, navigation]);

  // Show loading while checking auth
  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Loading...
        </Text>
      </View>
    );
  }

  // Don't render admin screen if user is not authenticated or not an admin
  if (!user || user.userType !== 'admin') {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Redirecting...
        </Text>
      </View>
    );
  }

  // Only render admin screen for authenticated admin users
  return (
    <ScreenWithHeader isAdminPage={true} requireAuth={true}>
      <Admin {...props} />
    </ScreenWithHeader>
  );
}

function AddVenueScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <AddVenue {...props} />
    </ScreenWithHeader>
  );
}

function MyReportsScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <MyReports {...props} />
    </ScreenWithHeader>
  );
}

function TourPlannerScreen(props) {
  return (
    <ScreenWithHeader>
      <TourPlanner {...props} />
    </ScreenWithHeader>
  );
}

function AboutUsScreen(props) {
  return (
    <ScreenWithHeader>
      <AboutUs {...props} />
    </ScreenWithHeader>
  );
}

function PlansScreen(props) {
  return (
    <ScreenWithHeader>
      <Plans {...props} />
    </ScreenWithHeader>
  );
}

function SubscriptionSuccessScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <SubscriptionSuccess {...props} />
    </ScreenWithHeader>
  );
}



function ContactScreen(props) {
  return (
    <ScreenWithHeader>
      <Contact {...props} />
    </ScreenWithHeader>
  );
}

function CommunityScreenContainer(props) {
  return (
    <ScreenWithHeader footerBg="#1075ae">
      <CommunityScreen {...props} />
    </ScreenWithHeader>
  );
}

function CommunityListScreen(props) {
  const communityListRef = useRef(null);
  
  const handleScroll = (event) => {
    // Call CommunityList's scroll handler if it exists
    if (communityListRef.current?.handleScrollInternal) {
      communityListRef.current.handleScrollInternal(event);
    }
  };

  return (
    <ScreenWithHeader footerBg="#1075ae" onScroll={handleScroll}>
      <CommunityList {...props} ref={communityListRef} />
    </ScreenWithHeader>
  );
}

function UserProfileScreenContainer(props) {
  return (
    <ScreenWithHeader>
      <UserProfileScreen {...props} />
    </ScreenWithHeader>
  );
}

function OrganizationProfileScreenContainer(props) {
  return (
    <ScreenWithHeader>
      <OrganizationProfileScreen {...props} />
    </ScreenWithHeader>
  );
}

function NavigationContent() {
  const { user, loading } = useUser();

  return (
    <Stack.Navigator 
      initialRouteName={user ? "Home" : "Login"}
      screenOptions={{
        headerShown: false,
        contentStyle: { backgroundColor: 'transparent' },
        animation: 'slide_from_right',
        presentation: 'card'
      }}
    >
      {/* Landing screen that bypasses login */}
      <Stack.Screen 
        name="Landing" 
        component={LandingScreenContainer} 
        options={{
          gestureEnabled: false,
          animationEnabled: false
        }}
      />
      
      <Stack.Group>
        <Stack.Screen 
          name="Login" 
          component={LoginScreen} 
          options={{
            gestureEnabled: false,
            animationEnabled: false
          }}
        />
        <Stack.Screen 
          name="Waitlist" 
          component={WaitlistScreen}
          options={{
            gestureEnabled: false,
            animationEnabled: false
          }}
        />
      </Stack.Group>

      <Stack.Group screenOptions={{ gestureEnabled: true }}>
        <Stack.Screen name="Home" component={HomeScreen} />
        <Stack.Screen name="Profile" component={ProfileScreen} />
        <Stack.Screen name="MyReports" component={MyReportsScreen} />
        <Stack.Screen name="VenueList" component={VenueListScreen} />
        <Stack.Screen name="Venue" component={VenueDetailScreen} />
        <Stack.Screen name="VenueSubmission" component={VenueSubmissionScreen} />
        <Stack.Screen name="Admin" component={AdminScreen} />
        <Stack.Screen name="AddVenue" component={AddVenueScreen} />
        <Stack.Screen name="TourPlanner" component={TourPlannerScreen} />
        <Stack.Screen name="AboutUs" component={AboutUsScreen} />
        <Stack.Screen name="Plans" component={PlansScreen} />
        <Stack.Screen name="SubscriptionSuccess" component={SubscriptionSuccessScreen} />


        <Stack.Screen name="Contact" component={ContactScreen} />
        <Stack.Screen name="CommunityScreen" component={CommunityScreenContainer} />
        <Stack.Screen name="CommunityList" component={CommunityListScreen} />
        <Stack.Screen name="UserProfileScreen" component={UserProfileScreenContainer} />
        <Stack.Screen name="OrganizationProfileScreen" component={OrganizationProfileScreenContainer} />


      </Stack.Group>
    </Stack.Navigator>
  );
}

export default function Navigation() {
  const navigationRef = useRef();
  const [dimensions, setDimensions] = React.useState({
    window: Dimensions.get('window')
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions({ window });
    });
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    if (navigationRef.current) {
      navigationService.setNavigator(navigationRef.current);
    }
  }, [navigationRef.current]);

  return (
    <NavigationContainer 
      ref={navigationRef}
      linking={Platform.OS === 'web' ? linking : undefined}
      fallback={<LoadingScreen />}
    >
      <LoginPersistenceProvider>
        <VenueProvider>
          <CommunityProvider>
            <CommunityModalProvider>
              <NavigationContent />
            </CommunityModalProvider>
          </CommunityProvider>
        </VenueProvider>
      </LoginPersistenceProvider>
    </NavigationContainer>
  );
}

function LoadingScreen() {
  return (
    <View style={[globalStyles.container, { justifyContent: 'center', alignItems: 'center' }]}>
      <BackgroundVideo />
      <View style={[globalStyles.overlay, { justifyContent: 'center', alignItems: 'center' }]}>
        <Image
          source={require('../../assets/logos/logo.png')}
          style={{ width: 150, height: 150, marginBottom: 20 }}
        />
        <ActivityIndicator size="large" color="#ffffff" />
      </View>
    </View>
  );
} 